import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { GroupSummaryReport } from 'src/app/core/models/reports/reports.model';
import { SummaryReportsService } from 'src/app/core/services/reports/summary/summary-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  GROUP_CONSTS,
  GROUP_PDF_REPORT,
  GROUP_REPORT_FILE_NAME,
  GroupSummaryFormGroup,
} from './group-summary.constants';

@Component({
  selector: 'app-group-summary',
  templateUrl: './group-summary.component.html',
  styleUrls: ['./group-summary.component.scss'],
  standalone: false,
})
export class GroupSummaryComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly data: GroupSummaryFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private dialogRef: MatDialogRef<GroupSummaryComponent> = inject(
    MatDialogRef<GroupSummaryComponent>,
  );
  private fb: FormBuilder = inject(FormBuilder);
  private summaryReportsService: SummaryReportsService = inject(
    SummaryReportsService,
  );
  private reportUtils: ReportsUtils = inject(ReportsUtils);
  private commonUtils: CommonUtils = inject(CommonUtils);

  readonly CONSTS = GROUP_CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly OPTIONS = Options;
  readonly PDF_REPORT = GROUP_PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  groupSummaryForm!: FormGroup;
  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;

  groupSummaryReport!: GroupSummaryReport;
  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit(): void {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 30,
    );
    this.maxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() + 14,
    );

    this.groupSummaryForm = this.fb.group({
      date: [this.currDate],
      reportFormat: [Options.PDF],
    });
  }

  onExit() {
    this.commonUtils.openExitModal(this.groupSummaryForm.dirty);
  }

  radioChange(newValue: Options) {
    this.groupSummaryForm.get('reportFormat')?.setValue(newValue);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof GroupSummaryComponent
   */
  getReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const formValue = this.groupSummaryForm.value;

          const groupCode = user?.locationGroup;
          const date = this.commonUtils.getFormattedDate(formValue.date);

          const request = {
            groupCode,
            date,
          };
          return this.summaryReportsService.getGroupSummaryReport(request).pipe(
            take(1),
            map((data) => {
              const formatType =
                this.groupSummaryForm.get('reportFormat')?.value;
              const groupSummary = data.response.groupSummary;
              if (groupSummary?.length) {
                if (formatType === Options.CSV) {
                  const customHeader = [
                    [
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      'GROUP SUMMARY',
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                    ],
                    [
                      '',
                      '',
                      '',
                      `${data.response.groupName}`,
                      '',
                      '',
                      `${data.response.groupCode}`,
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      '',
                      `${data.response.date}`,
                    ],
                  ];
                  const formatedDate = formatDate(
                    new Date(),
                    'MM-dd-yyyy',
                    'en-US',
                  );
                  const customFooter = [
                    [
                      'TOTAL',
                      '',
                      `${data?.response.totalStops}`,
                      `${data?.response.totalPkgs}`,
                      `${data?.response.totalDeliveryStopsNet}`,
                      `${data?.response.totalDeliveryPkgsNet}`,
                      `${data?.response.totalPickupStopsNet}`,
                      `${data?.response.totalPickupPkgsNet}`,
                      `${data?.response.totalMissedPickup}`,
                      `${data?.response.totalMissedDelStop}`,
                      `${data?.response.totalMissedDelPkgs}`,
                      `${data?.response.totalSendAgainCod}`,
                      `${data?.response.totalSendAgainPaid}`,
                      `${data?.response.totalNetCod}`,
                      `${data?.response.totalOffload}`,
                      `${data?.response.totalOpen}`,
                      `${data?.response.totalOther}`,
                    ],
                    ['Printed:', `${formatedDate}`],
                  ];
                  this.reportUtils.exportCSVReport(
                    groupSummary,
                    GROUP_REPORT_FILE_NAME,
                    customHeader,
                    customFooter,
                  );
                }
                if (formatType === Options.PDF) {
                  this.groupSummaryReport = data;
                  setTimeout(() => {
                    const content =
                      this.pdfContainer.element.nativeElement.innerHTML;
                    this.reportUtils.exportPdfReport(
                      content,
                      GROUP_PDF_REPORT.NAME,
                      GROUP_REPORT_FILE_NAME,
                      'l',
                    );
                  }, 0);
                }
              } else {
                this.commonUtils.updateBannerType(
                  BANNER_TYPE.NO_DATA_AVAILABLE,
                );
              }

              this.dialogRef.close();
            }),
          );
        }),
      )
      .subscribe();
  }
}
